/**
 * Double authentification : ajouter MyEquip à l'application d'authentification
 * et garder ses codes de secours. Partagé par la connexion (activation imposée
 * au premier accès) et par Mon compte (activation, changement d'application,
 * nouveaux codes).
 *
 * Enrôlement simplifié le 12/10/2026 : beaucoup de personnes ne savaient pas
 * quoi faire du QR code quand elles étaient déjà sur leur téléphone, ou
 * tapaient leur mot de passe à la place du code. Sur un appareil tactile,
 * « Ajouter à mon application » ouvre directement l'application
 * d'authentification (lien `otpauth://`) et le QR code reste à un appui pour un
 * autre appareil ; ailleurs, le QR code est affiché d'emblée. La clé se copie
 * et se masque ; une URI brute ne remplace jamais le QR code (§26.10).
 */
import { useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { Check, Copy, Download, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { t } from '../../core/i18n.js';
import { Button } from '../ui.js';
import { ButtonLink } from '../ui/action.js';

export interface MfaSetupData {
  otpauthUri: string;
  secret: string;
}

function CopyButton({ text, label }: { text: string; label: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <Button variant="secondary" onClick={() => {
      void navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }}>
      {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      {copied ? t('commun.copie') : label}
    </Button>
  );
}

/** Ajout de MyEquip à l'application : lien direct au téléphone, QR code, clé manuelle. */
export function MfaPairing({ setup }: { setup: MfaSetupData }): ReactNode {
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [showKey, setShowKey] = useState(false);
  const [touch] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches);
  const [showQr, setShowQr] = useState(!touch);
  useEffect(() => {
    if (!showQr) return;
    void QRCode.toDataURL(setup.otpauthUri, { width: 220, margin: 2 }).then(setQrDataUrl);
  }, [setup.otpauthUri, showQr]);
  return (
    <div className="mfa-pairing">
      {touch ? (
        <>
          <span>{t('auth.mfaEtapeTelephone')}</span>
          <ButtonLink href={setup.otpauthUri} variant="secondary">
            <ShieldCheck size={18} aria-hidden="true" /> {t('auth.mfaAjouterApp')}
          </ButtonLink>
        </>
      ) : null}
      {showQr ? (
        <>
          <span>{touch ? t('auth.mfaEtapeAutreAppareil') : t('auth.mfaScannezQr')}</span>
          <div className="mfa-pairing__qr">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={t('auth.mfaQrAlt')} width={220} height={220} />
            ) : (
              <span className="skeleton mfa-pairing__qr-placeholder" />
            )}
          </div>
        </>
      ) : (
        <button type="button" className="text-link mfa-pairing__show" onClick={() => setShowQr(true)}>
          {t('auth.mfaAfficherQr')}
        </button>
      )}
      <span className="muted">{t('auth.mfaCleManuelle')}</span>
      <div className="mfa-pairing__key">
        <code className="mono mfa-pairing__secret">
          {showKey ? setup.secret : '•'.repeat(Math.min(setup.secret.length, 24))}
        </code>
        <Button variant="ghost" ariaLabel={showKey ? t('commun.masquer') : t('commun.afficher')}
          onClick={() => setShowKey((v) => !v)}>
          {showKey ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </Button>
        <CopyButton text={setup.secret} label={t('commun.copier')} />
      </div>
    </div>
  );
}

/** Les trois étapes : ouvrir l'appli, y ajouter MyEquip, saisir le code. Le titre est celui de la page. */
export function MfaSetupPanel({ setup }: { setup: MfaSetupData }): ReactNode {
  return (
    <ol className="mfa-setup__steps">
      <li>{t('auth.mfaEtape1')}</li>
      <li><MfaPairing setup={setup} /></li>
      <li>{t('auth.mfaEtapeCode')}</li>
    </ol>
  );
}

/** Les codes de secours, à copier ou télécharger : affichés une seule fois. */
export function BackupCodes({ codes }: { codes: readonly string[] }): ReactNode {
  const download = (): void => {
    const blob = new Blob(
      [`Codes de secours MyEquip — ${new Date().toLocaleDateString('fr-FR')}\n\n${codes.join('\n')}\n\nChaque code permet une connexion unique.`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'myequip-codes-secours.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="mfa-backup">
      <ul className="mfa-backup__codes" aria-label={t('auth.codesSecoursTitre')}>
        {codes.map((code) => <li key={code} className="mono">{code}</li>)}
      </ul>
      <div className="mfa-backup__actions">
        <CopyButton text={codes.join('\n')} label={t('auth.codesSecoursCopier')} />
        <Button variant="secondary" onClick={download}>
          <Download size={16} aria-hidden="true" /> {t('auth.codesSecoursTelecharger')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Écran bloquant des codes de secours : on ne continue qu'après avoir confirmé
 * les avoir gardés (à la connexion, le profil n'est rechargé qu'après — sinon le
 * routeur démonterait l'écran et perdrait les codes).
 */
export function BackupCodesGate({ codes, onDone, continueLabel, framed = true }: {
  codes: readonly string[];
  onDone: () => void;
  /** Libellé du bouton final (« Accéder à l'application » par défaut). */
  continueLabel?: string;
  /** Carte autonome (connexion) ; sans cadre dans une page qui en a déjà un. */
  framed?: boolean;
}): ReactNode {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className={framed ? 'card stack anim-page mfa-gate mfa-gate--framed' : 'stack mfa-gate'}>
      <div className="mfa-gate__head">
        <ShieldCheck size={22} aria-hidden="true" className="mfa-gate__icon" />
        <h2 className="mfa-gate__title">{t('auth.codesSecoursTitre')}</h2>
      </div>
      <p className="muted mfa-gate__text">{t('auth.codesSecoursExplication')}</p>
      <BackupCodes codes={codes} />
      <label className="mfa-gate__confirm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>{t('auth.codesSecoursConfirmation')}</span>
      </label>
      <Button large disabled={!confirmed} onClick={onDone}>
        {continueLabel ?? t('auth.codesSecoursContinuer')}
      </Button>
    </div>
  );
}
